import { useMemo, useState } from "react";
import type { TFunction } from "i18next";
import { Plus, X } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import { AlertList } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { FormActions } from "@jbnl/shared-ui";
import {
  useHerstelSpeelschema,
  useSetSpeelschema,
  type Regelinvoer,
  type Wedstrijdbaan,
  type Wedstrijddag,
} from "./useWedstrijden";

/** The rows of one baan, keyed on the baan so the table can look a column up by id. */
type Kolommen = Record<string, Regelinvoer[]>;

const uurEnMinuut = (time: string) => time.slice(0, 5);

/**
 * Columns because that is the shape the boekje prints, but a speelschema belongs to a baan and
 * not the dag (ADR-0039): on the landelijke finale two klassen run different intervals on one
 * date. Typing does not save - several rows only mean anything together.
 */
export function Speelschema({
  seizoenId,
  dag,
}: {
  seizoenId: string;
  dag: Wedstrijddag;
}) {
  const { t } = useTranslation(["wedstrijden", "common"]);
  const opslaan = useSetSpeelschema(seizoenId);
  const herstel = useHerstelSpeelschema(seizoenId);

  // Whichever of the two failed. One line, so the guard below and the sentence it shows
  // cannot drift apart.
  const fout = opslaan.error ?? herstel.error;

  // What the server holds, as rows the form can edit.
  const opgeslagen = useMemo<Kolommen>(
    () =>
      Object.fromEntries(
        dag.wedstrijdbanen.map((baan) => [
          baan.id,
          baan.speelschema.map((regel) => ({
            id: regel.id,
            time: uurEnMinuut(regel.time),
            label: regel.label,
          })),
        ]),
      ),
    [dag.wedstrijdbanen],
  );

  // `null` is "nothing typed yet", and reads through to whatever the server holds. Keeping the
  // unedited state absent rather than copying the server's answer into it is what lets a refetch
  // land without an effect to re-seed from - the same reasoning BezettingGrid records.
  const [bewerkt, setBewerkt] = useState<Kolommen | null>(null);
  const kolommen = bewerkt ?? opgeslagen;

  const vuil =
    bewerkt !== null && JSON.stringify(bewerkt) !== JSON.stringify(opgeslagen);

  // The union of every baan's times, which is the dag-shaped table the boekje renders. A dag
  // whose banen run different grids - Brandbestrijding beside een LD-klasse - shows a sparse
  // table, and that is the truth about the dag rather than a fault in the drawing.
  const tijden = useMemo(
    () =>
      [
        ...new Set(
          Object.values(kolommen)
            .flat()
            .map((r) => r.time),
        ),
      ].sort(),
    [kolommen],
  );

  const afwijkend = dag.wedstrijdbanen.filter((baan) => !baan.volgtStandaard);

  function verzet(volgende: Kolommen) {
    setBewerkt(volgende);
  }

  function wijzigTijd(oud: string, nieuw: string) {
    if (!nieuw) return;

    // Retimes the row in every baan that has it. That is what retiming a dag means, and it is
    // visible: both columns move together in front of the beheerder.
    verzet(
      Object.fromEntries(
        Object.entries(kolommen).map(([baanId, regels]) => [
          baanId,
          regels.map((r) => (r.time === oud ? { ...r, time: nieuw } : r)),
        ]),
      ),
    );
  }

  function wijzigLabel(baanId: string, time: string, label: string) {
    verzet({
      ...kolommen,
      [baanId]: kolommen[baanId].map((r) =>
        r.time === time ? { ...r, label: label || null } : r,
      ),
    });
  }

  function voegToe(baanId: string, time: string) {
    verzet({
      ...kolommen,
      [baanId]: [...kolommen[baanId], { id: null, time, label: null }],
    });
  }

  function haalWeg(baanId: string, time: string) {
    verzet({
      ...kolommen,
      [baanId]: kolommen[baanId].filter((r) => r.time !== time),
    });
  }

  /** A new time on every baan of the dag, which is what a dag-wide extra slot is. */
  function voegRijToe() {
    const laatste = tijden.at(-1) ?? "08:00";
    const nieuw = eenUurLater(laatste, tijden);

    verzet(
      Object.fromEntries(
        Object.entries(kolommen).map(([baanId, regels]) => [
          baanId,
          [...regels, { id: null, time: nieuw, label: null }],
        ]),
      ),
    );
  }

  function bewaar() {
    opslaan.mutate(
      {
        dagId: dag.id,
        banen: dag.wedstrijdbanen.map((baan) => ({
          wedstrijdbaanId: baan.id,
          regels: [...kolommen[baan.id]].sort((a, b) =>
            a.time.localeCompare(b.time),
          ),
        })),
      },
      // Back to reading through to the server, which now holds the same rows - so the button
      // disables itself rather than staying lit over an edit that has already landed.
      { onSuccess: () => setBewerkt(null) },
    );
  }

  if (dag.wedstrijdbanen.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("speelschema.title")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Above the table, because it explains the cell a beheerder is about to type in. The
            placeholder says the same thing, and a placeholder disappears the moment you start
            typing - which is exactly when the question comes up. */}
        <p className="text-sm text-muted-foreground">
          <Trans
            t={t}
            i18nKey="speelschema.uitleg"
            components={{ em: <em /> }}
          />
        </p>

        <SpeelschemaAfwijkendWaarschuwing afwijkend={afwijkend} t={t} />

        <SpeelschemaTabel
          banen={dag.wedstrijdbanen}
          kolommen={kolommen}
          tijden={tijden}
          t={t}
          onWijzigTijd={wijzigTijd}
          onWijzigLabel={wijzigLabel}
          onVoegToe={voegToe}
          onHaalWeg={haalWeg}
        />

        <Button variant="secondary" onClick={voegRijToe}>
          <Plus />
          {t("speelschema.rijToevoegen")}
        </Button>

        <SpeelschemaOpslaanActies
          t={t}
          fout={fout}
          vuil={vuil}
          opslaan={opslaan}
          onBewaar={bewaar}
        />

        <SpeelschemaHerstellen
          banen={dag.wedstrijdbanen}
          herstel={herstel}
          t={t}
          onHersteld={() => setBewerkt(null)}
        />
      </CardContent>
    </Card>
  );
}

/** The banen whose speelschema no longer matches the standaard, named so a beheerder knows which
 * ones to check before trusting the table below. */
function SpeelschemaAfwijkendWaarschuwing({
  afwijkend,
  t,
}: {
  afwijkend: Wedstrijdbaan[];
  t: TFunction<["wedstrijden", "common"]>;
}) {
  if (afwijkend.length === 0) return null;

  return (
    <AlertList
      tone="warning"
      title={t("speelschema.afwijkend", { count: afwijkend.length })}
      description={t("speelschema.afwijkendUitleg")}
    >
      {/* Named in a list rather than woven into the sentence: a dag runs up to four banen
          - Bruinisse already does - and four labels inside one sentence is the shape that
          stops being scannable first. */}
      <ul className="list-disc space-y-0.5 ps-5">
        {afwijkend.map((baan) => (
          <li key={baan.id}>{baan.label}</li>
        ))}
      </ul>
    </AlertList>
  );
}

type SpeelschemaHandlers = {
  onWijzigLabel: (baanId: string, time: string, label: string) => void;
  onVoegToe: (baanId: string, time: string) => void;
  onHaalWeg: (baanId: string, time: string) => void;
};

function SpeelschemaTabel({
  banen,
  kolommen,
  tijden,
  t,
  onWijzigTijd,
  ...handlers
}: {
  banen: Wedstrijdbaan[];
  kolommen: Kolommen;
  tijden: string[];
  t: TFunction<["wedstrijden", "common"]>;
  onWijzigTijd: (oud: string, nieuw: string) => void;
} & SpeelschemaHandlers) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">{t("speelschema.caption")}</caption>
        <SpeelschemaKop banen={banen} t={t} />
        <tbody>
          {tijden.map((time) => (
            <SpeelschemaRij
              key={time}
              time={time}
              banen={banen}
              kolommen={kolommen}
              t={t}
              onWijzigTijd={onWijzigTijd}
              {...handlers}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SpeelschemaKop({
  banen,
  t,
}: {
  banen: Wedstrijdbaan[];
  t: TFunction<["wedstrijden", "common"]>;
}) {
  return (
    <thead>
      <tr>
        <th scope="col" className="w-28 py-2 text-left font-medium">
          {t("speelschema.tijd")}
        </th>
        {banen.map((baan) => (
          <th
            key={baan.id}
            scope="col"
            className="px-3 py-2 text-left font-medium"
          >
            <span className="block">{baan.label}</span>
            <span className="block text-xs font-normal text-muted-foreground">
              {t("speelschema.plekken", { count: baan.capaciteit })}
            </span>
          </th>
        ))}
      </tr>
    </thead>
  );
}

function SpeelschemaRij({
  time,
  banen,
  kolommen,
  t,
  onWijzigTijd,
  ...handlers
}: {
  time: string;
  banen: Wedstrijdbaan[];
  kolommen: Kolommen;
  t: TFunction<["wedstrijden", "common"]>;
  onWijzigTijd: (oud: string, nieuw: string) => void;
} & SpeelschemaHandlers) {
  return (
    <tr className="border-t">
      <th scope="row" className="py-2 pe-3 text-left font-normal">
        <Input
          type="time"
          value={time}
          aria-label={t("speelschema.tijdVeld", { tijd: time })}
          onChange={(event) => onWijzigTijd(time, event.target.value)}
        />
      </th>
      {banen.map((baan) => (
        <SpeelschemaCel
          key={baan.id}
          baan={baan}
          time={time}
          regel={kolommen[baan.id].find((r) => r.time === time)}
          t={t}
          {...handlers}
        />
      ))}
    </tr>
  );
}

function SpeelschemaCel({
  baan,
  time,
  regel,
  t,
  onWijzigLabel,
  onVoegToe,
  onHaalWeg,
}: {
  baan: Wedstrijdbaan;
  time: string;
  regel: Regelinvoer | undefined;
  t: TFunction<["wedstrijden", "common"]>;
} & SpeelschemaHandlers) {
  return (
    <td className="px-3 py-2">
      {regel ? (
        <span className="flex items-center gap-1">
          {/* One input, and an empty one means available. A checkbox beside a text field would
              be two ways to say the same thing. */}
          <Input
            value={regel.label ?? ""}
            maxLength={60}
            placeholder={t("speelschema.vrij")}
            aria-label={t("speelschema.cel", { baan: baan.label, tijd: time })}
            onChange={(event) =>
              onWijzigLabel(baan.id, time, event.target.value)
            }
          />
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t("speelschema.celWeghalen", {
              baan: baan.label,
              tijd: time,
            })}
            onClick={() => onHaalWeg(baan.id, time)}
          >
            <X />
          </Button>
        </span>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          aria-label={t("speelschema.celToevoegen", {
            baan: baan.label,
            tijd: time,
          })}
          onClick={() => onVoegToe(baan.id, time)}
        >
          <Plus />
          {t("speelschema.toevoegen")}
        </Button>
      )}
    </td>
  );
}

/** The error, the dirty flag and the save button together: all three describe the same pending
 * save, and belong on the same line so their meaning does not have to be assembled from parts. */
function SpeelschemaOpslaanActies({
  t,
  fout,
  vuil,
  opslaan,
  onBewaar,
}: {
  t: TFunction<["wedstrijden", "common"]>;
  fout: Error | null;
  vuil: boolean;
  opslaan: ReturnType<typeof useSetSpeelschema>;
  onBewaar: () => void;
}) {
  return (
    <FormActions>
      {/* Left of the button, so appearing or clearing never shifts what the reader aims at. */}
      {fout && <p className="text-sm text-destructive">{fout.message}</p>}
      {vuil && !opslaan.isPending && (
        <span className="text-sm text-muted-foreground">
          {t("speelschema.vuil")}
        </span>
      )}
      <Button onClick={onBewaar} disabled={!vuil || opslaan.isPending}>
        {opslaan.isPending
          ? t("common:actions.busy")
          : t("speelschema.opslaan")}
      </Button>
    </FormActions>
  );
}

function SpeelschemaHerstellen({
  banen,
  herstel,
  t,
  onHersteld,
}: {
  banen: Wedstrijdbaan[];
  herstel: ReturnType<typeof useHerstelSpeelschema>;
  t: TFunction<["wedstrijden", "common"]>;
  onHersteld: () => void;
}) {
  const zonderStandaard = banen.filter((baan) => !baan.heeftStandaard);

  return (
    <>
      <div className="flex flex-wrap gap-2 border-t pt-4">
        {banen
          .filter((baan) => baan.heeftStandaard)
          .map((baan) => (
            <ConfirmDelete
              key={baan.id}
              trigger={
                <Button
                  variant="outline"
                  size="sm"
                  disabled={baan.volgtStandaard || herstel.isPending}
                >
                  {t("speelschema.herstellen", { baan: baan.label })}
                </Button>
              }
              title={t("speelschema.herstelVraag", { baan: baan.label })}
              description={t("speelschema.herstelDescription")}
              confirmLabel={t("speelschema.herstelConfirm")}
              onConfirm={() =>
                herstel.mutate(baan.id, { onSuccess: onHersteld })
              }
            />
          ))}
      </div>

      {zonderStandaard.length > 0 && (
        <p className="text-sm text-muted-foreground">
          {t("speelschema.geenStandaard", {
            banen: zonderStandaard.map((baan) => baan.label).join(", "),
          })}
        </p>
      )}
    </>
  );
}

/**
 * An hour after the last, nudged until free. An hour rather than the grid's own interval: those
 * are richttijden JBNL sets, and guessing one is the arithmetic ADR-0039 rejected.
 */
function eenUurLater(laatste: string, bezet: string[]) {
  const [uur, minuut] = laatste.split(":").map(Number);
  let volgende = (uur + 1) * 60 + minuut;

  while (bezet.includes(alsTijd(volgende)) && volgende < 24 * 60 - 1) {
    volgende += 5;
  }

  return alsTijd(volgende);
}

const alsTijd = (minuten: number) =>
  `${String(Math.floor(Math.min(minuten, 24 * 60 - 1) / 60)).padStart(2, "0")}:` +
  `${String(Math.min(minuten, 24 * 60 - 1) % 60).padStart(2, "0")}`;
